import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DATA_SYNCING_WEBSOCKET_STATUS, LOCAL_STORAGE_KEYS } from "../shared/constants";

interface DataSyncWebSocketProps {
  userId?: string;
  // Optional callback on task complete
  onComplete?: () => void;
  // Optional callback on task failure
  onError?: (error?: unknown) => void;
}

type ProgressPayload = {
  progress?: number;
  status?: string;
  message?: string;
  task_id?: string | number;
  total_tasks?: number;
  completed_tasks?: number;
  timestamp?: string | number;
  stage?: string;
  completed?: boolean;
  error?: string | Record<string, unknown> | null;
};

export const useDataSyncingWebsocket = ({
  userId,
  onComplete,
  onError,
}: DataSyncWebSocketProps) => {
  const wsUrl = import.meta.env.VITE_WEB_SOCKET_URL;

  const [wsConnected, setWsConnected] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [status, setStatus] = useState<string>("idle");
  const [message, setMessage] = useState<string>("");
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [lastEvent, setLastEvent] = useState<ProgressPayload | null>(null);
  const [completedTasks, setCompletedTasks] = useState<number>(0);
  const [totalTasks, setTotalTasks] = useState<number>(0);
  const [taskId, setTaskId] = useState<string>("");

  const wsRef = useRef<WebSocket | null>(null);
  const currentKeyRef = useRef<string | null>(null);
  const isOpenRef = useRef<boolean>(false);

  const connectionKey = useMemo(() => {
    if (!wsUrl || !userId) return null;
    return `${wsUrl}|${userId}|${taskId ?? ""}`;
  }, [wsUrl, userId, taskId]);

  const close = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      // eslint-disable-next-line no-console
      console.log("[DataSyncWS] Manual close() invoked", {
        readyState: ws.readyState,
        isOpen: isOpenRef.current,
      });
      ws.close();
    } catch {
      // noop
    }
    wsRef.current = null;
    isOpenRef.current = false;
  }, []);

  useEffect(() => {
    // Require wsUrl to establish the connection
    if (!wsUrl || !userId) {
      try {
        // eslint-disable-next-line no-console
        console.warn("[DataSyncWS] Missing wsUrl or userId; skipping connection", {
          hasWsUrl: Boolean(wsUrl),
          hasUserId: Boolean(userId),
        });
      } catch {}
      return;
    }

    // Avoid duplicating connection for the same key
    if (
      currentKeyRef.current === connectionKey &&
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      try {
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] Reusing existing socket; preventing duplicate connection", {
          connectionKey,
          readyState: wsRef.current?.readyState,
        });
      } catch {}
      return;
    }

    // Close existing connection if key changed
    if (wsRef.current && currentKeyRef.current !== connectionKey) {
      try {
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] Closing existing socket due to connectionKey change", {
          previousKey: currentKeyRef.current,
          nextKey: connectionKey,
          prevReadyState: wsRef.current.readyState,
        });
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
      isOpenRef.current = false;
    }

    currentKeyRef.current = connectionKey;

    // Require taskId to subscribe to task progress
    if (!taskId) {
      try {
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] No taskId provided; skipping connection");
      } catch {}
      return;
    }
    
    const fullUrl = `${wsUrl}/task-progress/?user_id=${userId}&task_id=${taskId}`;
    try {
      // eslint-disable-next-line no-console
      console.log("[DataSyncWS] Creating WebSocket", { fullUrl, connectionKey });
    } catch {}

    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      isOpenRef.current = true;
      setWsConnected(true);
      // reset state when (re)connecting
      setIsCompleted(false);
      setIsError(false);
      setStatus("in_progress");
      try {
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] Socket opened", {
          url: (ws as any)?.url,
          readyState: ws.readyState,
        });
      } catch {}
    };

    ws.onclose = (evt) => {
      isOpenRef.current = false;
      setWsConnected(false);
      try {
        // eslint-disable-next-line no-console
        console.warn("[DataSyncWS] Socket closed", {
          code: evt.code,
          reason: evt.reason,
          wasClean: (evt as CloseEvent).wasClean,
        });
      } catch {}
    };

    ws.onerror = (evt) => {
      // Better diagnostics on socket errors
      try {
        // eslint-disable-next-line no-console
        console.error("[DataSyncWS] Socket error", {
          event: evt,
          readyState: ws.readyState,
          url: (ws as any)?.url,
        });
      } catch {}
      setIsError(true);
      if (onError) onError(evt);
    };

    ws.onmessage = (evt) => {
      try {
        const data: ProgressPayload = JSON.parse(evt.data || "{}");
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] Message received", data?.progress);
        setLastEvent(data);

        if (typeof data.progress === "number") setProgress(data.progress);
        if (typeof data.status === "string") setStatus(data.status);
        if (typeof data.message === "string") setMessage(data.message);
        if (typeof data.completed_tasks === "number") setCompletedTasks(data.completed_tasks);
        if (typeof data.total_tasks === "number") setTotalTasks(data.total_tasks);

        if (data.error) {
          setIsError(true);
          try {
            // eslint-disable-next-line no-console
            console.warn("[DataSyncWS] Error payload received", { error: data.error });
          } catch {}
          if (onError) onError(data.error);
        }

        const countsDone =
          typeof data.completed_tasks === "number" &&
          typeof data.total_tasks === "number" &&
          data.total_tasks > 0 &&
          data.completed_tasks >= data.total_tasks;

        if (data.status === DATA_SYNCING_WEBSOCKET_STATUS.COMPLETED) {
          setIsCompleted(true);
          try {
            // eslint-disable-next-line no-console
            console.log("[DataSyncWS] Completion detected", {
              countsDone,
              status: data.status,
            });
          } catch {}
          if (onComplete) onComplete();
        }
      } catch (e) {
        try {
          // eslint-disable-next-line no-console
          console.warn("[DataSyncWS] Failed to parse message payload", {
            raw: evt.data,
            error: e,
          });
        } catch {}
        // ignore invalid payloads
      }
    };

    return () => {
      try {
        // eslint-disable-next-line no-console
        console.log("[DataSyncWS] Effect cleanup: closing socket if open", {
          readyState: ws.readyState,
        });
        ws.close();
      } catch {}
      wsRef.current = null;
      isOpenRef.current = false;
    };
  }, [wsUrl, userId, taskId]);

  useEffect(() => {
   const storedTaskId = localStorage.getItem(LOCAL_STORAGE_KEYS.TASK_ID)
   if(storedTaskId){
    setTaskId(storedTaskId)
   }
  }, [])

  const reset = useCallback(() => {
    try {
      // eslint-disable-next-line no-console
      console.log("[DataSyncWS] reset() state to initial values", {
        prev: {
          progress,
          status,
          message,
          isCompleted,
          isError,
          completedTasks,
          totalTasks,
        },
      });
    } catch {}
    setProgress(0);
    setStatus("idle");
    setMessage("");
    setIsCompleted(false);
    setIsError(false);
    setLastEvent(null);
    setCompletedTasks(0);
    setTotalTasks(0);
  }, []);

  return {
    wsConnected,
    progress,
    status,
    message,
    isCompleted,
    isError,
    lastEvent,
    completedTasks,
    totalTasks,
    close,
    reset,
    taskId,
    setTaskId
  } as const;
};
